import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Close as CloseIcon,
  PhotoCamera,
  Save as SaveIcon,
} from "@mui/icons-material";
import api from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import { formatarCPF, formatarCNPJ, formatarTelefone } from '../../utils/formatadores.js';
import { TIPO_PESSOA_OPTIONS } from '../../utils/constants.js';
import Preloader from '../../components/Preloader';

export default function DadosEmpresa() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [empresaId, setEmpresaId] = useState(null);

  const [formData, setFormData] = useState({
    st_nome: "",
    st_tipoPessoa: TIPO_PESSOA_OPTIONS[1].value,
    st_cpfCnpj: "",
    st_email: "",
    st_telefone: "",
    st_endereco: "",
    st_observacoes: "",
    st_logotipo: "",
    bo_ativo: true,
    nu_max_dependentes: 4,
    bo_permite_substituicao: false,
    nu_meses_carencia: 12,
    nu_vigencia_contrato: 12
  });

  const [formErrors, setFormErrors] = useState({
    st_nome: false,
    st_cpfCnpj: false,
    st_email: false,
  });

  useEffect(() => {
    carregarEmpresa();
  }, []);

  const carregarEmpresa = async () => {
    setInitialLoading(true);
    try {
      const response = await api.get('/empresa');
      const dados = response.data.dados || response.data || [];

      if (dados.length > 0) {
        const empresa = dados[0];
        setEmpresaId(empresa.id_empresa || empresa.id);

        setFormData({
          // ... fields
          st_nome: empresa.st_nome || "",
          st_tipoPessoa: empresa.st_tipoPessoa || TIPO_PESSOA_OPTIONS[1].value,
          st_cpfCnpj: empresa.st_cpfCnpj || "",
          st_email: empresa.st_email || "",
          st_telefone: empresa.st_telefone || "",
          st_endereco: empresa.st_endereco || "",
          st_observacoes: empresa.st_observacoes || "",
          st_logotipo: empresa.st_logotipo || "",
          bo_ativo: empresa.bo_ativo !== undefined ? empresa.bo_ativo : true,
          nu_max_dependentes: empresa.nu_max_dependentes !== undefined ? empresa.nu_max_dependentes : 4,
          bo_permite_substituicao: empresa.bo_permite_substituicao !== undefined ? empresa.bo_permite_substituicao : false,
          nu_meses_carencia: empresa.nu_meses_carencia !== undefined ? empresa.nu_meses_carencia : 12,
          nu_vigencia_contrato: empresa.nu_vigencia_contrato !== undefined ? empresa.nu_vigencia_contrato : 12,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
      setError('Erro ao carregar dados da empresa');
    } finally {
      setInitialLoading(false);
    }
  };

  // ... rest of the functions ...

  // Use Preloader for initial loading
  if (initialLoading) {
    return <Preloader message="Carregando dados da empresa..." />;
  }

  // ... rest of the render (return Box...)


  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    let valorFormatado = value;

    if (name === 'st_cpfCnpj') {
      if (formData.st_tipoPessoa === TIPO_PESSOA_OPTIONS[0].value) {
        valorFormatado = formatarCPF(value);
      } else {
        valorFormatado = formatarCNPJ(value);
      }
    } else if (name === 'st_telefone') {
      valorFormatado = formatarTelefone(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : valorFormatado
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError("A imagem deve ter no máximo 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          st_logotipo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const validarFormulario = () => {
    const novosErros = {
      st_nome: !formData.st_nome,
      st_cpfCnpj: !formData.st_cpfCnpj,
    };

    const cpfCnpjNumbers = formData.st_cpfCnpj.replace(/\D/g, '');
    if (formData.st_tipoPessoa === 'FISICA' && cpfCnpjNumbers.length !== 11) {
      novosErros.st_cpfCnpj = true;
    }
    if (formData.st_tipoPessoa === 'JURIDICA' && cpfCnpjNumbers.length !== 14) {
      novosErros.st_cpfCnpj = true;
    }

    if (formData.st_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.st_email)) {
      novosErros.st_email = true;
    }

    setFormErrors(novosErros);
    return !Object.values(novosErros).some(erro => erro);
  };

  const salvarDados = async () => {
    if (!validarFormulario()) {
      setError("Por favor, preencha todos os campos obrigatórios");
      return;
    }

    if (!user || !['superusuario', 'gerente'].includes(user.perfil)) {
      setError("Você não tem permissão para alterar os dados da empresa.");
      return;
    }

    try {
      setLoading(true);

      const observacoesProcessadas = formData.st_observacoes
        ? (typeof formData.st_observacoes === 'string' ? formData.st_observacoes.trim() : formData.st_observacoes)
        : null;

      const dadosFormatados = {
        ...formData,
        st_cpfCnpj: formData.st_cpfCnpj.replace(/\D/g, ""),
        st_telefone: formData.st_telefone.replace(/\D/g, ""),
        st_observacoes: observacoesProcessadas || null
      };

      Object.keys(dadosFormatados).forEach(key => {
        if (key !== 'st_observacoes' && (dadosFormatados[key] === "" || dadosFormatados[key] === undefined)) {
          dadosFormatados[key] = null;
        }
      });

      if (empresaId) {
        await api.put(`/empresa/${empresaId}`, dadosFormatados);
        setSuccess("Dados da empresa atualizados com sucesso!");
      } else {
        await api.post('/empresa', dadosFormatados);
        setSuccess("Empresa cadastrada com sucesso!");
        carregarEmpresa(); // Reload to get ID
      }

    } catch (error) {
      if (error.response?.status === 401) {
        setError("Sessão expirada. Por favor, faça login novamente.");
      } else {
        setError(error.response?.data?.mensagem || error.response?.data?.error || "Erro ao salvar dados da empresa");
      }
    } finally {
      setLoading(false);
    }
  };

  // Only allowed profiles can view/edit
  if (user && !['superusuario', 'gerente'].includes(user.perfil)) {
    // Optional: Redirect or show message
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 3 }
      }}
    >
      {/* Notificações */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 40, color: '#20ac9c' }} />
          <Typography variant="h4" component="h1" sx={{ color: '#20ac9c', fontWeight: 'bold' }}>
            Dados da Empresa
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Dados Básicos" />
            <Tab label="Contato" />
            <Tab label="Configurações" />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Logotipo Upload */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={formData.st_logotipo || "https://via.placeholder.com/150?text=Logo"}
                    sx={{
                      width: 150,
                      height: 150,
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      p: 1
                    }}
                  />
                  <Button
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: -10,
                      right: -10,
                      borderRadius: '50%',
                      minWidth: 40,
                      width: 40,
                      height: 40,
                      padding: 0,
                      bgcolor: '#20ac9c',
                      color: 'white',
                      '&:hover': { bgcolor: '#167a6f' },
                      boxShadow: 2
                    }}
                    disabled={loading}
                  >
                    <PhotoCamera fontSize="small" />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {formData.st_logotipo && (
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        '&:hover': { bgcolor: '#ffebee' }
                      }}
                      onClick={() => setFormData(prev => ({ ...prev, st_logotipo: "" }))}
                      disabled={loading}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Nome/Razão Social"
                name="st_nome"
                value={formData.st_nome}
                onChange={handleInputChange}
                required
                error={formErrors.st_nome}
                helperText={formErrors.st_nome ? "Nome é obrigatório" : ""}
                disabled={loading}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Pessoa</InputLabel>
                    <Select
                      name="st_tipoPessoa"
                      value={formData.st_tipoPessoa}
                      onChange={handleInputChange}
                      label="Tipo de Pessoa"
                      required
                      disabled={loading}
                    >
                      {TIPO_PESSOA_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label={formData.st_tipoPessoa === TIPO_PESSOA_OPTIONS[0].value ? "CPF" : "CNPJ"}
                    name="st_cpfCnpj"
                    value={formData.st_cpfCnpj}
                    onChange={handleInputChange}
                    required
                    error={formErrors.st_cpfCnpj}
                    helperText={formErrors.st_cpfCnpj ? "CPF/CNPJ é obrigatório" : ""}
                    disabled={loading}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Email"
                name="st_email"
                type="email"
                value={formData.st_email}
                onChange={handleInputChange}
                error={formErrors.st_email}
                helperText={formErrors.st_email ? "Email inválido" : ""}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Telefone"
                name="st_telefone"
                value={formData.st_telefone}
                onChange={handleInputChange}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Endereço"
                name="st_endereco"
                value={formData.st_endereco}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={loading}
              />
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Observações"
                name="st_observacoes"
                value={formData.st_observacoes}
                onChange={handleInputChange}
                multiline
                rows={4}
                disabled={loading}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.bo_ativo}
                    onChange={handleInputChange}
                    name="bo_ativo"
                    disabled={loading}
                  />
                }
                label="Ativo"
              />
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Regras de Negócio - Dependentes
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                O objetivo é permitir que o titular cadastre até 4 dependentes, mas sem a possibilidade de substituição ao longo dos 12 meses de contrato.
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Máx. Dependentes"
                    name="nu_max_dependentes"
                    value={formData.nu_max_dependentes}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    helperText="Padrão: 4"
                    disabled={loading}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Carência Subst. Dependentes (Meses)"
                    name="nu_meses_carencia"
                    value={formData.nu_meses_carencia}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    helperText="Padrão: 12"
                    disabled={loading}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Vigência do Contrato (Meses)"
                    name="nu_vigencia_contrato"
                    value={formData.nu_vigencia_contrato}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    helperText="Padrão: 12"
                    disabled={loading}
                  />
                </Box>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.bo_permite_substituicao}
                      onChange={handleInputChange}
                      name="bo_permite_substituicao"
                      disabled={loading}
                    />
                  }
                  label="Permitir Substituição (Ignora Carência)"
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Se ativado, permite trocar dependentes a qualquer momento. Se desativado, respeita a regra de "vaga queimada" e carência.
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              onClick={salvarDados}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                bgcolor: '#20ac9c',
                '&:hover': { bgcolor: '#167a6f' },
                py: 1.5,
                px: 4
              }}
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </Box>

        </Paper>
      </Box>
    </Box>
  );
}
